/**
 * 分镜资源 API 服务
 * 对接后端 /api/v1/storyboard-resources
 */
import apiClient from './client';
import { uploadDirectToOss } from './direct-upload';
import type { StoryboardItem } from '@/types/storyboard';

/** 分镜图片响应（驼峰格式，与后端 ImageResponse 对应） */
export interface StoryboardImageResponse {
  imageId: string;
  storyboardId: string;
  planId?: string | null;
  /** @deprecated 已迁移为 planId，保留兼容旧数据 */
  tagId?: string | null;
  /** 根原始图片 ID（null 表示本身即为一级图片） */
  parentId?: string | null;
  /** 直接前驱图片 ID */
  sourceId?: string | null;
  /** 产生本记录的编辑动作类型 */
  editAction?: number | null;
  name?: string;
  description?: string;
  url: string;
  status: number;
  statusStr: string;
  isActive: boolean;
  version: number;
  width?: number;
  height?: number;
  fileSize?: number;
  format?: string;
  checksum: string;
  storagePath: string;
  storageProvider?: string;
  aiModel?: string;
  aiPrompt?: string;
  aiPromptId?: string;
  aiParams?: Record<string, unknown>;
  humanAssetType?: number | null;
  humanAssetStatus?: number | null;
  errorMessage?: string;
  createdAt: string | null;
  completedAt?: string;
}

export interface ImportStoryboardImageFromReferenceAssetBoardPayload {
  sourceResourceType: 1 | 2;
  sourceResourceId: string;
  planId?: string;
  name?: string;
  description?: string;
}

export type ExtractFramePosition = 'first' | 'current' | 'last';

export interface ExtractFramePayload {
  targetStoryboardId: string;
  framePosition: ExtractFramePosition;
  timestampSeconds?: number;
}

/** POST extract-frame 成功 data（精简字段） */
export interface ExtractFrameResponse {
  storyboardId: string;
  planId: string;
  imageId?: string;
}

/** GET videos/{id}/versions 单条（后端 snake_case 原样） */
export interface StoryboardVideoVersionItem {
  /** 历史行的 videoId；当前选中行（isCurrent=true）为 null */
  videoId: string | null;
  version: number;
  action: number | null;
  resolution?: string | null;
  duration?: string | number | null;
  frameRate?: number | null;
  url?: string;
  coverUrl?: string | null;
  fileSize?: number | null;
  format?: string | null;
  aiModel?: string | null;
  aiPrompt?: string | null;
  aiParams?: Record<string, unknown> | null | undefined;
  createdAt: string | null;
  isCurrent: boolean;
}

/** POST videos/{id}/upscale 返回 data */
export interface StoryboardVideoUpscaleResponse {
  jobId?: string;
  job_id?: string;
  taskId?: string;
  task_id?: string;
  status?: string;
  estimatedCredits?: unknown;
  estimated_credits?: unknown;
}

export function resolveStoryboardVideoUpscaleJobId(
  data: StoryboardVideoUpscaleResponse | Record<string, unknown> | null | undefined
): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const rec = data as Record<string, unknown>;
  const j = rec.jobId ?? rec.job_id;
  return typeof j === 'string' && j.trim() ? j.trim() : undefined;
}

/** 分镜视频响应（驼峰格式，与后端 VideoResponse 对应） */
export interface StoryboardVideoResponse {
  videoId: string;
  storyboardId: string;
  planId?: string | null;
  parentId?: string | null;
  sourceId?: string | null;
  sourceVideoId?: string | null;
  sourceRootVideoId?: string | null;
  sourceOriginId?: string | null;
  editAction?: number | null;
  name?: string;
  description?: string;
  url: string;
  coverUrl?: string;
  status: number;
  statusStr: string;
  isActive: boolean;
  isFinalized: boolean;
  version: number;
  displayOrder: number;
  duration?: string | number | null;
  resolution?: string | null;
  frameRate?: number | null;
  fileSize?: number | null;
  format?: string | null;
  checksum: string;
  storageProvider?: string;
  storagePath: string;
  aiModel?: string;
  aiPrompt?: string | null;
  aiParams?: Record<string, unknown>;
  humanAssetType?: number | null;
  humanAssetStatus?: number | null;
  errorMessage?: string | null;
  progress?: number | null;
  createdAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface StoryboardVideoTrimPayload {
  trimStart: number;
  trimEnd: number;
}

export interface RelatedResourceItemResponse {
  resourceId: string;
  resourceUrl: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
}

export interface RelatedTagGroupResponse {
  tagId: string;
  tagLabel: string;
  resources: RelatedResourceItemResponse[];
}

export interface RelatedElementResponse {
  type: 'character' | 'location' | 'prop' | 'footage';
  elementId: string;
  elementName: string;
  hasTags: boolean;
  tags?: RelatedTagGroupResponse[];
  defaultResource?: RelatedResourceItemResponse;
}

export interface RelatedResourcesResponse {
  resources: RelatedElementResponse[];
}

/** 对白角色音色（来自分镜关联的当前角色标签 meta_data.voice） */
export interface DialogueVoiceResponse {
  voiceId?: string | null;
  providerVoiceId?: string | null;
  voiceName?: string | null;
}

export interface StoryboardDialogueResponse {
  dialogueId: string;
  storyboardId: string;
  characterId?: string;
  /** 角色音色来源标签ID（前端用于编辑音色时定位可写入的装扮记录） */
  characterTagId?: string | null;
  tagLabel?: string | null;
  tag_label?: string | null;
  characterName?: string;
  characterImageUrl?: string | null;
  content: string;
  dialogueType: number;
  /** @deprecated 后端将废弃，前端请仅使用 dialogueType */
  dialogueTypeStr?: string;
  sequenceOrder: number;
  startTime?: string | null;
  duration?: string | null;
  emotion?: string | null;
  notes?: string | null;
  /** 角色标签音色（来自分镜关联的当前角色标签，无则 null） */
  voice?: DialogueVoiceResponse | null;
  createdAt: string;
  updatedAt: string;
}

export interface DialogueVoiceoverResponse {
  voiceoverId: string;
  dialogueId: string;
  storyboardId: string;
  audioUrl: string;
  status: number;
  statusStr: string;
  isActive: boolean;
  voiceId: string;
  voiceName?: string | null;
  speed: string | number;
  volume: string | number;
  pitch: string | number;
  duration?: string | number | null;
  fileSize?: number | null;
  format?: string | null;
  checksum: string;
  storageProvider?: string | null;
  storagePath: string;
  errorMessage?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface StoryboardSoundEffectResponse {
  soundEffectId: string;
  storyboardId: string;
  audioUrl: string;
  status: number;
  statusStr?: string;
  isActive: boolean;
  version: number;
  effectType: number;
  effectName?: string;
  volume: string | number;
  fadeInDuration: string | number;
  fadeOutDuration: string | number;
  loopEnabled: boolean;
  startTime: string | number;
  duration?: string | number;
  format?: string | null;
  aiPrompt?: string;
}

export interface CreateStoryboardSoundEffectPayload {
  audioUrl: string;
  checksum: string;
  storagePath: string;
  effectType: number;
  effectName?: string;
  volume?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  loopEnabled?: boolean;
  startTime?: number;
  duration?: number;
  fileSize?: number;
  format?: string;
  sampleRate?: number;
  bitRate?: number;
  storageProvider?: string;
  aiModel?: string;
  aiPrompt?: string;
  aiParams?: Record<string, unknown>;
  status?: number;
  isActive?: boolean;
}

export interface UpdateStoryboardSoundEffectPayload {
  effectName?: string;
  volume?: number;
  fadeInDuration?: number;
  fadeOutDuration?: number;
  loopEnabled?: boolean;
  startTime?: number;
  duration?: number;
}

export interface DialogueSyncItem {
  dialogueId?: string;
  characterId?: string;
  characterTagId?: string | null;
  characterName?: string;
  content: string;
  sequenceOrder?: number;
  dialogueType?: number;
  startTime?: number;
  duration?: number;
  emotion?: string;
  notes?: string;
}

export interface CreateStoryboardDialoguePayload {
  characterId?: string | null;
  characterTagId?: string | null;
  characterName?: string | null;
  content: string;
  dialogueType?: number;
  emotion?: string | null;
  notes?: string | null;
}

export type UpdateStoryboardDialoguePayload = Partial<CreateStoryboardDialoguePayload>;

export interface DialogueSyncResponse {
  createdCount: number;
  updatedCount: number;
  deletedCount: number;
  dialogues: StoryboardDialogueResponse[];
  linkedItems?: StoryboardItem[];
}

export interface DialogueLinkedElementsPayload {
  characters?: Array<string | LinkedElementItemPayload>;
  locations?: Array<string | LinkedElementItemPayload>;
  props?: Array<string | LinkedElementItemPayload>;
}

export interface LinkedElementItemPayload {
  elementId?: string;
  tagId?: string;
  name?: string;
  tagLabel?: string;
}

const dialogueVoiceoversInFlight = new Map<string, Promise<DialogueVoiceoverResponse[]>>();

export const storyboardResourcesApi = {
  async downloadImageBinary(imageId: string): Promise<Blob> {
    return apiClient.get(`/storyboard-resources/images/${imageId}/binary`, {
      responseType: 'blob',
      timeout: 120_000,
    });
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/related-resources
   * 获取分镜关联资源（用于 @ 提及）
   */
  async getRelatedResources(
    storyboardId: string,
    params?: { search?: string }
  ): Promise<RelatedResourcesResponse> {
    const data = (await apiClient.get(
      `/storyboard-resources/storyboards/${storyboardId}/related-resources`,
      {
        params: {
          search: params?.search,
        },
      }
    )) as Partial<RelatedResourcesResponse>;

    return {
      resources: Array.isArray(data.resources) ? data.resources : [],
    };
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/images
   * 获取分镜的所有图片版本
   *
   * @param planId - 按方案 ID 过滤
   * @param rootOnly - true 时只返回一级图片（parentId 为 null）；默认 true；sourceId 传入时忽略
   * @param sourceId - 按 sourceId 过滤溯源链路，返回直接派生自该图片的所有记录
   */
  async getStoryboardImages(
    storyboardId: string,
    planId?: string,
    rootOnly?: boolean,
    sourceId?: string
  ): Promise<StoryboardImageResponse[]> {
    const searchParams = new URLSearchParams();
    if (planId) searchParams.set('planId', planId);
    if (rootOnly !== undefined) searchParams.set('rootOnly', String(rootOnly));
    if (sourceId) searchParams.set('sourceId', sourceId);
    const query = searchParams.toString();
    const data = (await apiClient.get(
      `/storyboard-resources/storyboards/${storyboardId}/images${query ? `?${query}` : ''}`
    )) as StoryboardImageResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/videos
   * 获取分镜的所有视频版本
   */
  async getStoryboardVideos(
    storyboardId: string,
    params?: { planId?: string | null }
  ): Promise<StoryboardVideoResponse[]> {
    const data = (await apiClient.get(`/storyboard-resources/storyboards/${storyboardId}/videos`, {
      params: {
        planId: params?.planId ?? undefined,
      },
    })) as StoryboardVideoResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * PUT /api/v1/storyboard-resources/storyboards/{storyboard_id}/videos/order
   * 重排当前视频方案下的视频；activeVideoIds 传入时同步主用/备用状态
   */
  async reorderStoryboardVideos(
    storyboardId: string,
    videoIds: string[],
    activeVideoIds?: string[]
  ): Promise<StoryboardVideoResponse[]> {
    const data = (await apiClient.put(
      `/storyboard-resources/storyboards/${storyboardId}/videos/order`,
      { videoIds, ...(activeVideoIds ? { activeVideoIds } : {}) }
    )) as StoryboardVideoResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/v1/storyboard-resources/videos/{video_id}/trim-status
   * 获取单个裁剪视频状态和假进度
   */
  async getStoryboardVideoTrimStatus(videoId: string): Promise<StoryboardVideoResponse> {
    return apiClient.get(
      `/storyboard-resources/videos/${videoId}/trim-status`
    ) as Promise<StoryboardVideoResponse>;
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/dialogues
   * 获取分镜对白列表
   */
  async getStoryboardDialogues(storyboardId: string): Promise<StoryboardDialogueResponse[]> {
    const data = (await apiClient.get(
      `/storyboard-resources/storyboards/${storyboardId}/dialogues`
    )) as StoryboardDialogueResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * GET /api/v1/storyboard-resources/dialogues/{dialogue_id}/voiceovers
   * 获取对白配音版本列表
   */
  async getDialogueVoiceovers(dialogueId: string): Promise<DialogueVoiceoverResponse[]> {
    const inFlight = dialogueVoiceoversInFlight.get(dialogueId);
    if (inFlight) return inFlight;

    const request = (async () => {
      const data = (await apiClient.get(
        `/storyboard-resources/dialogues/${dialogueId}/voiceovers`
      )) as DialogueVoiceoverResponse[];
      return Array.isArray(data) ? data : [];
    })();

    dialogueVoiceoversInFlight.set(dialogueId, request);
    try {
      return await request;
    } finally {
      dialogueVoiceoversInFlight.delete(dialogueId);
    }
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/voiceovers
   * 获取分镜下全部配音版本
   */
  async getStoryboardVoiceovers(storyboardId: string): Promise<DialogueVoiceoverResponse[]> {
    const data = (await apiClient.get(
      `/storyboard-resources/storyboards/${storyboardId}/voiceovers`
    )) as DialogueVoiceoverResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * POST /api/v1/storyboard-resources/voiceovers/{voiceover_id}/activate
   * 设置当前对白的激活配音
   */
  async activateDialogueVoiceover(voiceoverId: string): Promise<DialogueVoiceoverResponse> {
    return apiClient.post(
      `/storyboard-resources/voiceovers/${voiceoverId}/activate`
    ) as Promise<DialogueVoiceoverResponse>;
  },

  /**
   * DELETE /api/v1/storyboard-resources/voiceovers/{voiceover_id}
   * 删除单条配音版本
   */
  async deleteDialogueVoiceover(voiceoverId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/voiceovers/${voiceoverId}`);
  },

  /**
   * PUT /api/v1/storyboard-resources/storyboards/{storyboard_id}/dialogues/sync
   * 全量同步分镜对白
   */
  async syncStoryboardDialogues(
    storyboardId: string,
    dialogues: DialogueSyncItem[],
    linkedElements?: DialogueLinkedElementsPayload
  ): Promise<DialogueSyncResponse> {
    return apiClient.put(`/storyboard-resources/storyboards/${storyboardId}/dialogues/sync`, {
      dialogues,
      linkedElements,
    }) as Promise<DialogueSyncResponse>;
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboard_id}/dialogues
   * 创建分镜对白
   */
  async createStoryboardDialogue(
    storyboardId: string,
    payload: CreateStoryboardDialoguePayload
  ): Promise<StoryboardDialogueResponse> {
    return apiClient.post(
      `/storyboard-resources/storyboards/${storyboardId}/dialogues`,
      payload
    ) as Promise<StoryboardDialogueResponse>;
  },

  /**
   * PATCH /api/v1/storyboard-resources/dialogues/{dialogue_id}
   * 更新分镜对白
   */
  async updateStoryboardDialogue(
    dialogueId: string,
    payload: UpdateStoryboardDialoguePayload
  ): Promise<StoryboardDialogueResponse> {
    return apiClient.patch(
      `/storyboard-resources/dialogues/${dialogueId}`,
      payload
    ) as Promise<StoryboardDialogueResponse>;
  },

  /**
   * DELETE /api/v1/storyboard-resources/dialogues/{dialogue_id}
   * 删除分镜对白
   */
  async deleteStoryboardDialogue(dialogueId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/dialogues/${dialogueId}`);
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboard_id}/dialogues/reorder
   * 调整分镜对白顺序
   */
  async reorderStoryboardDialogues(storyboardId: string, orderedIds: string[]): Promise<void> {
    const dialogueOrders = Object.fromEntries(
      orderedIds.map((dialogueId, index) => [dialogueId, index + 1])
    );

    await apiClient.post(`/storyboard-resources/storyboards/${storyboardId}/dialogues/reorder`, {
      dialogueOrders,
    });
  },

  /**
   * GET /api/v1/storyboard-resources/storyboards/{storyboard_id}/sound-effects
   * 获取分镜的所有音效
   */
  async getStoryboardSoundEffects(storyboardId: string): Promise<StoryboardSoundEffectResponse[]> {
    const data = (await apiClient.get(
      `/storyboard-resources/storyboards/${storyboardId}/sound-effects`
    )) as StoryboardSoundEffectResponse[];
    return Array.isArray(data) ? data : [];
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboard_id}/sound-effects
   * 创建分镜音效
   */
  async createStoryboardSoundEffect(
    storyboardId: string,
    payload: CreateStoryboardSoundEffectPayload
  ): Promise<StoryboardSoundEffectResponse> {
    return apiClient.post(
      `/storyboard-resources/storyboards/${storyboardId}/sound-effects`,
      payload
    ) as Promise<StoryboardSoundEffectResponse>;
  },

  /**
   * PATCH /api/v1/storyboard-resources/sound-effects/{sound_effect_id}
   * 更新音效参数
   */
  async updateStoryboardSoundEffect(
    soundEffectId: string,
    payload: UpdateStoryboardSoundEffectPayload
  ): Promise<StoryboardSoundEffectResponse> {
    return apiClient.patch(
      `/storyboard-resources/sound-effects/${soundEffectId}`,
      payload
    ) as Promise<StoryboardSoundEffectResponse>;
  },

  /**
   * DELETE /api/v1/storyboard-resources/sound-effects/{sound_effect_id}
   * 删除音效
   */
  async deleteStoryboardSoundEffect(soundEffectId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/sound-effects/${soundEffectId}`);
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboard_id}/images/upload
   * 上传自定义分镜图片
   */
  async uploadStoryboardImage(
    storyboardId: string,
    file: File,
    name?: string,
    description?: string,
    planId?: string,
    onUploadProgress?: (percent: number) => void,
    options?: {
      sourceId?: string;
      editAction?: number;
    }
  ): Promise<StoryboardImageResponse> {
    const trimmedName = name?.trim();
    const trimmedDescription = description?.trim();
    const trimmedPlanId = planId?.trim();
    const trimmedSourceId = options?.sourceId?.trim();
    const editAction = options?.editAction;
    return uploadDirectToOss<StoryboardImageResponse>({
      file,
      businessType: 'storyboard_image',
      businessPayload: {
        storyboardId,
        ...(trimmedName ? { name: trimmedName } : {}),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(trimmedPlanId ? { planId: trimmedPlanId } : {}),
        ...(trimmedSourceId ? { sourceId: trimmedSourceId } : {}),
        ...(editAction != null ? { editAction } : {}),
      },
      onProgress: onUploadProgress,
    });
  },

  /**
   * POST /api/v1/storyboard-resources/storyboards/{storyboard_id}/images/import
   * 从素材板源资源导入为当前分镜图片版本
   */
  async importStoryboardImageFromReferenceAssetBoard(
    storyboardId: string,
    payload: ImportStoryboardImageFromReferenceAssetBoardPayload
  ): Promise<StoryboardImageResponse> {
    return apiClient.post(
      `/storyboard-resources/storyboards/${storyboardId}/images/import`,
      payload
    ) as Promise<StoryboardImageResponse>;
  },

  /**
   * PATCH /api/v1/storyboard-resources/images/{image_id}
   * 更新分镜图片（name, description, isActive）
   */
  async updateStoryboardImage(
    imageId: string,
    payload: { name?: string; description?: string; isActive?: boolean }
  ): Promise<StoryboardImageResponse> {
    return apiClient.patch(
      `/storyboard-resources/images/${imageId}`,
      payload
    ) as Promise<StoryboardImageResponse>;
  },

  /**
   * POST /api/v1/storyboard-resources/images/{image_id}/activate
   * 设置激活图片（切换版本）
   */
  async activateStoryboardImage(imageId: string): Promise<StoryboardImageResponse> {
    return apiClient.post(
      `/storyboard-resources/images/${imageId}/activate`
    ) as Promise<StoryboardImageResponse>;
  },

  /**
   * POST /api/v1/storyboard-resources/videos/{video_id}/activate
   * 加入主用视频（兼容旧接口）
   */
  async activateStoryboardVideo(videoId: string): Promise<StoryboardVideoResponse> {
    return apiClient.post(
      `/storyboard-resources/videos/${videoId}/activate`
    ) as Promise<StoryboardVideoResponse>;
  },

  /**
   * POST /api/v1/storyboard-resources/videos/{video_id}/extract-frame
   * 从已完成视频截取指定帧写入目标分镜
   */
  async extractStoryboardVideoFrame(
    videoId: string,
    payload: ExtractFramePayload
  ): Promise<ExtractFrameResponse> {
    return apiClient.post(`/storyboard-resources/videos/${videoId}/extract-frame`, {
      targetStoryboardId: payload.targetStoryboardId,
      framePosition: payload.framePosition,
      ...(payload.timestampSeconds != null ? { timestampSeconds: payload.timestampSeconds } : {}),
    }) as Promise<ExtractFrameResponse>;
  },

  /**
   * POST /api/v1/storyboard-resources/videos/{video_id}/trim
   * 提交分镜视频裁剪任务，返回新建的 processing 视频
   */
  async trimStoryboardVideo(
    videoId: string,
    payload: StoryboardVideoTrimPayload
  ): Promise<StoryboardVideoResponse> {
    return apiClient.post(
      `/storyboard-resources/videos/${videoId}/trim`,
      payload
    ) as Promise<StoryboardVideoResponse>;
  },

  /**
   * PATCH /api/v1/storyboard-resources/videos/{video_id}
   * 更新分镜视频（name, description, isActive, isFinalized）
   */
  async updateStoryboardVideo(
    videoId: string,
    payload: { name?: string; description?: string; isActive?: boolean; isFinalized?: boolean }
  ): Promise<StoryboardVideoResponse> {
    return apiClient.patch(
      `/storyboard-resources/videos/${videoId}`,
      payload
    ) as Promise<StoryboardVideoResponse>;
  },

  /**
   * DELETE /api/v1/storyboard-resources/videos/{video_id}?scope=single|lineage
   * single（默认）：只删当前视频，若为激活/选中版本则后端自动回退到最新剩余版本。
   * lineage：删除整条溯源链（root + 所有子版本），清空方案选中状态。
   */
  async deleteStoryboardVideo(
    videoId: string,
    options?: { scope?: 'single' | 'lineage' }
  ): Promise<void> {
    const params = options?.scope ? { scope: options.scope } : undefined;
    await apiClient.delete(`/storyboard-resources/videos/${videoId}`, { params });
  },

  /**
   * POST /api/v1/storyboard-resources/videos/{video_id}/upscale
   * 发起视频超清，返回 AI jobId 供轮询
   */
  async upscaleStoryboardVideo(
    videoId: string,
    params?: { resolution?: string; model?: string }
  ): Promise<StoryboardVideoUpscaleResponse> {
    return apiClient.post(
      `/storyboard-resources/videos/${videoId}/upscale`,
      params ?? {}
    ) as Promise<StoryboardVideoUpscaleResponse>;
  },

  /**
   * GET /api/v1/storyboard-resources/videos/{video_id}/versions
   * 视频版本历史（当前 + 归档快照）
   */
  async getStoryboardVideoVersions(videoId: string): Promise<StoryboardVideoVersionItem[]> {
    const data = await apiClient.get<StoryboardVideoVersionItem[]>(
      `/storyboard-resources/videos/${videoId}/versions`
    );
    return Array.isArray(data) ? data : [];
  },

  /**
   * DELETE /api/v1/storyboard-resources/videos/{video_id}/versions/{target_video_id}
   * 删除溯源链中的历史视频行（不可删除当前选中版本）
   */
  async deleteStoryboardVideoVersion(videoId: string, targetVideoId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/videos/${videoId}/versions/${targetVideoId}`);
  },

  /**
   * POST /api/v1/storyboard-resources/videos/{video_id}/rollback
   * 切换到溯源链中指定的历史版本
   */
  async rollbackStoryboardVideoVersion(
    videoId: string,
    targetVideoId: string
  ): Promise<{
    videoId?: string;
    version?: number;
    coverUrl?: string;
  }> {
    return apiClient.post(`/storyboard-resources/videos/${videoId}/rollback`, {
      targetVideoId,
    }) as Promise<{
      videoId?: string;
      version?: number;
      coverUrl?: string;
    }>;
  },

  /**
   * DELETE /api/v1/storyboard-resources/images/{image_id}
   * 删除分镜图片（不能删除激活的图片）
   */
  async deleteStoryboardImage(imageId: string): Promise<void> {
    await apiClient.delete(`/storyboard-resources/images/${imageId}`);
  },
};
